import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { priceListFor, DEFAULT_CURRENCY } from '@/config/pricing';

/**
 * The price list this visitor is quoted.
 *
 * Starts on the bundled default so real prices render immediately, then swaps
 * to whatever GET /api/billing/pricing says. The server decides the currency
 * from the request's location — the client has no say, because local prices
 * are lower and a client-chosen currency would always be the cheapest one. The
 * server resolves it the same way at checkout, so the figure shown is the
 * figure charged. A failed request leaves the default list in place.
 *
 * The token is sent when there is one, but the endpoint is public: a signed-in
 * customer is quoted the currency stored on their account, which is what stops
 * a failed geo lookup flipping their prices to dollars.
 *
 * @returns {{ currency: string, symbol: string, locale: string, plans: Record<string, {monthly: number, yearly: number}> }}
 */
export function usePricing() {
  const { session } = useAuth();
  const [pricing, setPricing] = useState(() => priceListFor(DEFAULT_CURRENCY));

  useEffect(() => {
    let cancelled = false;
    const apiUrl = import.meta.env.VITE_API_URL ?? '';
    fetch(`${apiUrl}/api/billing/pricing`, {
      headers: session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.plans) setPricing(data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [session?.access_token]);

  return pricing;
}
